import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { MarketDataList, MarketDataRequest } from '@model/DxTrade.types'

const EMPTY_RESPONSE: MarketDataList = { events: [] }

type MarketDataEventsArgs = {
  symbols?: string[]
  eventTypes?: MarketDataRequest['eventTypes']
  account?: string
}

type UseMarketDataEventsResult = { isLoading: boolean; data: MarketDataList }

export const useMarketDataEvents = (args: MarketDataEventsArgs = {}): UseMarketDataEventsResult => {
  const { symbols, eventTypes, account } = args
  const apiClient = useApiClient(ClientApiClient)

  const symbolsSignature = symbols?.length ? Array.from(new Set(symbols)).sort().join('|') : ''

  const normalizedSymbols = useMemo(() => {
    if (!symbolsSignature) return undefined
    return symbolsSignature.split('|')
  }, [symbolsSignature])

  const shouldFetch = Boolean(normalizedSymbols?.length && eventTypes?.length && account)

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.marketDataEvents(symbolsSignature, eventTypes, account),
    queryFn: () =>
      apiClient.getMarketDataEvents({
        symbols: normalizedSymbols!,
        eventTypes: eventTypes!,
        account: account!,
      }),
    enabled: shouldFetch,
  })

  return {
    isLoading: shouldFetch ? isLoading : false,
    data: data ?? EMPTY_RESPONSE,
  }
}
